import { DateTime } from 'luxon'
import type { ReactNode } from 'react'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'

import type { BidType, MarketProgram } from '@/features/bidding/constants'
import { MARKET_TIMEZONE } from '@/features/bidding/constants'
import { isPtuEditable } from '@/features/bidding/utils/isPtuEditable'

type MarkPtuAsResetParams = {
  activationGroupUuid: string
  ptuStart: DateTime
  bidType: BidType
  marketProgram: MarketProgram
}

type State = {
  resetPtus: Map<string, Set<string>>
  markPtuAsReset: (params: MarkPtuAsResetParams) => boolean
  isPtuMarkedAsResetForActivationGroup: (activationGroupUuid: string, ptuStart: DateTime) => boolean
  isPtuMarkedAsReset: (ptuStart: DateTime) => boolean
}

const initialState: State = {
  resetPtus: new Map(),
  markPtuAsReset: () => false,
  isPtuMarkedAsResetForActivationGroup: () => false,
  isPtuMarkedAsReset: () => false,
}

const ResetPtusContext = createContext<State>(initialState)

/**
 * Tracks PTUs that have been automatically reset due to gate closure time.
 *
 * In intraday bidding, each PTU has a gate closure time after which it can no longer
 * be modified. While a user is actively editing bids, some PTUs may pass their gate
 * closure time and become uneditable. When this happens, the PTU values are reset
 * to their last submitted state (or zero for new bids).
 *
 * This context tracks which PTUs were reset so they can be visually highlighted
 * in the UI, alerting the user that their unsaved changes were reverted.
 *
 * The tracked PTUs are cleared when navigating to a different bidding session.
 */
function ResetPtusContextProvider({ children }: { children: ReactNode }) {
  const location = useLocation()
  const [resetPtus, setResetPtus] = useState<Map<string, Set<string>>>(new Map())

  // Track when the current bidding session started, this is used to determine if PTUs expired during this session
  const biddingSessionStartTime = useRef<DateTime>(DateTime.now().setZone(MARKET_TIMEZONE))

  // Clear reset PTUs and reset session start time when navigating to a different bidding session
  useEffect(() => {
    setResetPtus(new Map())
    biddingSessionStartTime.current = DateTime.now().setZone(MARKET_TIMEZONE)
  }, [location.search])

  /**
   * Attempts to mark a PTU as reset due to gate closure.
   *
   * Only marks the PTU if it was not marked before and if it expired during the current session (i.e., was editable
   * when the session started but is no longer editable now).
   * This prevents showing reset warnings multiple times or for PTUs that were already expired when the user opened the page.
   *
   * @returns true if the PTU was marked as reset, false if it was already marked as reset or if the PTU was already expired at session start
   */
  const markPtuAsReset = useCallback(
    ({ activationGroupUuid, ptuStart, bidType, marketProgram }: MarkPtuAsResetParams): boolean => {
      if (!isPtuEditable(biddingSessionStartTime.current, ptuStart, bidType, marketProgram)) {
        // PTU was already expired when the session started, don't mark it
        return false
      }
      if (isPtuMarkedAsResetForActivationGroup(activationGroupUuid, ptuStart)) {
        // PTU was already marked as reset, don't mark it again
        return false
      }

      setResetPtus((prev) => {
        const newMap = new Map(prev)
        const existing = newMap.get(activationGroupUuid) || new Set()
        const newSet = new Set(existing)
        newSet.add(ptuStart.toISO() ?? '')
        newMap.set(activationGroupUuid, newSet)
        return newMap
      })

      return true
    },
    [],
  )

  // Verify if a ptu has been marked as reset for a specific activation group.
  const isPtuMarkedAsResetForActivationGroup = useCallback(
    (activationGroupUuid: string, ptuStart: DateTime): boolean => {
      const ptuSet = resetPtus.get(activationGroupUuid)
      const ptuStartISO = ptuStart.toISO() ?? ''
      return ptuSet ? ptuSet.has(ptuStartISO) : false
    },
    [resetPtus],
  )

  // Verify if a ptu has been marked as reset for any activation group
  const isPtuMarkedAsReset = useCallback(
    (ptuStart: DateTime): boolean => {
      const ptuStartISO = ptuStart.toISO() ?? ''
      return resetPtus.values().some((ptuSet) => ptuSet.has(ptuStartISO))
    },
    [resetPtus],
  )

  const state = useMemo(
    () => ({
      resetPtus,
      markPtuAsReset,
      isPtuMarkedAsResetForActivationGroup,
      isPtuMarkedAsReset,
    }),
    [resetPtus, markPtuAsReset, isPtuMarkedAsResetForActivationGroup, isPtuMarkedAsReset],
  )

  return <ResetPtusContext.Provider value={state}>{children}</ResetPtusContext.Provider>
}

const useResetPtusContext = () => useContext(ResetPtusContext)

export { ResetPtusContextProvider, useResetPtusContext }
